import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import WifiIcon from '@mui/icons-material/Wifi';
import RouterIcon from '@mui/icons-material/Router';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { PRICES, presets, computeTotal, connectFee } from '../data/modules';
import { moduleColors } from '../theme';

const initialState = presets.find((p) => p.id === 'popular').state;

const OptionRow = ({ title, subtitle, price, color, checked, onChange, control }) => {
  const theme = useTheme();
  return (
    <Box
      onClick={control ? undefined : onChange}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        p: 2,
        borderRadius: 2,
        cursor: control ? 'default' : 'pointer',
        border: '1px solid',
        borderColor: checked ? alpha(color, 0.5) : theme.palette.divider,
        bgcolor: checked ? alpha(color, theme.palette.mode === 'dark' ? 0.1 : 0.06) : 'transparent',
        transition: 'all 0.2s ease',
        '&:hover': { borderColor: alpha(color, 0.5) },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Typography variant="subtitle2" sx={{ color, fontWeight: 700, whiteSpace: 'nowrap' }}>
          {price}
        </Typography>
        {control || (
          <Switch
            checked={checked}
            onChange={onChange}
            onClick={(e) => e.stopPropagation()}
            color="primary"
          />
        )}
      </Stack>
    </Box>
  );
};

const PlanCalculator = () => {
  const theme = useTheme();
  const [state, setState] = useState(initialState);
  const [feeAmount, setFeeAmount] = useState(3000);

  const { lines, total } = useMemo(() => computeTotal(state), [state]);
  const fee = connectFee(feeAmount);
  const net = Math.max(0, (Number(feeAmount) || 0) - fee);

  const applyPreset = (preset) => setState(preset.state);
  const toggle = (key) => setState((s) => ({ ...s, [key]: !s[key] }));
  const setEngine = (_, v) => v && setState((s) => ({ ...s, engine: v }));
  const setOlt = (delta) =>
    setState((s) => ({ ...s, oltCount: Math.max(0, Math.min(20, s.oltCount + delta)) }));

  const activePreset = presets.find(
    (p) => JSON.stringify(p.state) === JSON.stringify(state)
  )?.id;

  return (
    <Box>
      {/* Presets */}
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ mb: 4, flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}
      >
        {presets.map((p) => {
          const selected = activePreset === p.id;
          return (
            <Button
              key={p.id}
              onClick={() => applyPreset(p)}
              variant={selected ? 'contained' : 'outlined'}
              color={p.highlight ? 'primary' : 'inherit'}
              sx={{
                borderRadius: 99,
                borderColor: theme.palette.divider,
                color: selected ? undefined : 'text.primary',
                ...(p.highlight && !selected
                  ? { borderColor: theme.palette.primary.main, color: theme.palette.primary.main }
                  : {}),
              }}
              startIcon={selected ? <CheckRoundedIcon /> : undefined}
            >
              {p.label}
            </Button>
          );
        })}
      </Stack>

      <Grid container spacing={4}>
        {/* Builder */}
        <Grid item xs={12} md={7}>
          <Stack spacing={3.5}>
            {/* Step 1 - engine */}
            <Box>
              <Typography variant="overline" color="text.secondary">
                Step 1 — Choose your internet engine
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={state.engine}
                onChange={setEngine}
                fullWidth
                sx={{ mt: 1.5, gap: 1.5, '& .MuiToggleButton-root': { borderRadius: '12px !important', border: '1px solid', borderColor: 'divider', flexDirection: 'column', alignItems: 'flex-start', textTransform: 'none', p: 2 } }}
              >
                <ToggleButton value="internet">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <WifiIcon sx={{ color: moduleColors.core }} />
                    <Typography fontWeight={700}>Solvenda Internet</Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    €{PRICES.internet}/mo · Radius Manager, MikroTik…
                  </Typography>
                </ToggleButton>
                <ToggleButton value="radius">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <RouterIcon sx={{ color: moduleColors.radius }} />
                    <Typography fontWeight={700}>SolvendaRadius</Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    €{PRICES.radius}/mo · FreeRadius engine
                  </Typography>
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Most ISPs choose one engine, not both.
              </Typography>
            </Box>

            {/* Step 2 - services */}
            <Box>
              <Typography variant="overline" color="text.secondary">
                Step 2 — Add services
              </Typography>
              <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                <OptionRow
                  title="IPTV — Stalker Portal"
                  subtitle="Stalker / Ministra middleware"
                  price={`€${PRICES.iptv}/mo`}
                  color={moduleColors.core}
                  checked={state.iptvStalker}
                  onChange={() => toggle('iptvStalker')}
                />
                <OptionRow
                  title="IPTV — Xtream Codes"
                  subtitle="Xtream Codes middleware"
                  price={`€${PRICES.iptv}/mo`}
                  color={moduleColors.core}
                  checked={state.iptvXtream}
                  onChange={() => toggle('iptvXtream')}
                />
                <OptionRow
                  title="DVB-C"
                  subtitle="TVCAS, Conax encryption"
                  price={`€${PRICES.dvbc}/mo`}
                  color={moduleColors.core}
                  checked={state.dvbc}
                  onChange={() => toggle('dvbc')}
                />
              </Stack>
            </Box>

            {/* Step 3 - add-ons */}
            <Box>
              <Typography variant="overline" color="text.secondary">
                Step 3 — Add-ons
              </Typography>
              <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                <OptionRow
                  title="Fiscalization"
                  subtitle="Albanian fiscal invoices"
                  price={`€${PRICES.fiscal}/mo`}
                  color={moduleColors.fiscal}
                  checked={state.fiscal}
                  onChange={() => toggle('fiscal')}
                />
                <OptionRow
                  title="Solvenda DNS"
                  subtitle="DNS control & domain filtering"
                  price={`€${PRICES.dns}/mo`}
                  color={moduleColors.dns}
                  checked={state.dns}
                  onChange={() => toggle('dns')}
                />
                <OptionRow
                  title="Logs History"
                  subtitle="Legal log retention (2+ years)"
                  price={`€${PRICES.logs}/mo`}
                  color={moduleColors.logs}
                  checked={state.logs}
                  onChange={() => toggle('logs')}
                />
                <OptionRow
                  title="OLT Management"
                  subtitle="ZTE C320 / C300 / C600"
                  price={`€${PRICES.olt}/mo each`}
                  color={moduleColors.olt}
                  checked={state.oltCount > 0}
                  control={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <IconButton size="small" onClick={() => setOlt(-1)} sx={{ border: '1px solid', borderColor: 'divider' }}>
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Typography sx={{ width: 24, textAlign: 'center', fontWeight: 700 }}>
                        {state.oltCount}
                      </Typography>
                      <IconButton size="small" onClick={() => setOlt(1)} sx={{ border: '1px solid', borderColor: 'divider' }}>
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  }
                />
                <OptionRow
                  title="SolvendaConnect"
                  subtitle="Online card payments — no monthly fee"
                  price="€0/mo"
                  color={moduleColors.connect}
                  checked={state.connect}
                  onChange={() => toggle('connect')}
                />
              </Stack>
            </Box>
          </Stack>
        </Grid>

        {/* Summary */}
        <Grid item xs={12} md={5}>
          <Card
            sx={{
              position: { md: 'sticky' },
              top: { md: 100 },
              borderColor: alpha(theme.palette.primary.main, 0.4),
              background: `linear-gradient(160deg, ${alpha(theme.palette.primary.main, 0.08)}, ${theme.palette.background.paper} 60%)`,
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 3.5 } }}>
              <Typography variant="h5" component="h3" sx={{ mb: 2 }}>
                Your plan
              </Typography>
              <Stack spacing={1.25} sx={{ mb: 2 }}>
                {lines.map((l) => (
                  <Box key={l.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {l.label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {l.note ? (
                        <Chip label="per transaction" size="small" sx={{ height: 22 }} />
                      ) : (
                        `€${l.price}`
                      )}
                    </Typography>
                  </Box>
                ))}
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Total
                </Typography>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h3" component="div" sx={{ color: 'primary.main', fontWeight: 800 }}>
                    €{total}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    / month · no setup fee
                  </Typography>
                </Box>
              </Box>

              <Button
                component={Link}
                to="/contact"
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                sx={{ mt: 3 }}
              >
                Get this plan
              </Button>

              {state.connect && (
                <Box sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: alpha(moduleColors.connect, 0.08), border: `1px solid ${alpha(moduleColors.connect, 0.25)}` }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Connect fee preview
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    &lt; 5,000 ALL → 50 ALL flat · ≥ 5,000 ALL → 2% + 20 ALL
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    label="Payment amount (ALL)"
                    value={feeAmount}
                    onChange={(e) => setFeeAmount(e.target.value)}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">Our fee</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{fee.toLocaleString()} ALL</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">You receive</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: moduleColors.connect }}>
                      {net.toLocaleString()} ALL
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PlanCalculator;
